import { Link } from 'react-router-dom';
import {
  EGB_ENG_ID,
  COUNCIL_ROLES,
  COUNCIL_FUNCTIONS,
  CODING_STANDARDS,
  BACKEND_AREAS,
  FRONTEND_AREAS,
  DATABASE_AREAS,
  API_AREAS,
  AI_ENGINEERING,
  GIT_GOVERNANCE,
  CODE_REVIEW,
  STATIC_ANALYSIS,
  SECURE_CODING,
  TESTING_REQUIREMENTS,
  DEPENDENCY_GOVERNANCE,
  TECHNICAL_DEBT,
  CI_GATES,
  ENGINEERING_METRICS,
  KNOWLEDGE_SHARING,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_COUNCIL,
  DEMO_CODING,
  DEMO_STACK,
  DEMO_DATABASE_API,
  DEMO_AI_GIT,
  DEMO_REVIEW,
  DEMO_SECURE_TEST,
  DEMO_CI_METRICS,
  buildEngineeringDashboard,
} from '../../engineeringgovernance/framework';
import {
  EngineeringCouncilPanel,
  CodingConstitutionPanel,
  BackendFrontendPanel,
  DatabaseAPIPanel,
  AIEngineeringPanel,
  CodeReviewPanel,
  SecureTestingPanel,
  CIMetricsPanel,
} from '../../components/engineeringgovernance';
import { Card, KpiCard } from '../../components/ui';

export function EngineeringGovernancePage() {
  const dashboard = buildEngineeringDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EGB_ENG_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Engineering Governance</h1>
        <p className="mt-1 text-sm text-slate-500">
          Engineering Constitution for all software development — coding standards, AI-assisted development,
          quality assurance, and CI gates. Fully aligned with MPC-00001 and EGB-00001.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-100 dark:border-teal-900 dark:bg-teal-950 dark:text-teal-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Council Roles" value={dashboard.council_roles.toString()} />
        <KpiCard label="Coding Standards" value={dashboard.coding_standards.toString()} />
        <KpiCard label="CI Gates" value={dashboard.ci_gates.toString()} />
        <KpiCard label="Engineering Metrics" value={dashboard.engineering_metrics.toString()} />
        <KpiCard label="Testing Requirements" value={dashboard.testing_requirements.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Engineering Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Engineering Council</h2>
        <p className="mt-1 text-sm text-slate-500">{COUNCIL_ROLES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{COUNCIL_FUNCTIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <EngineeringCouncilPanel members={DEMO_COUNCIL} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Coding Constitution</h2>
        <p className="mt-1 text-sm text-slate-500">{CODING_STANDARDS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CodingConstitutionPanel standards={DEMO_CODING} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Backend & Frontend Engineering</h2>
        <p className="mt-1 text-sm text-slate-500">{BACKEND_AREAS.join(' · ')} · {FRONTEND_AREAS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BackendFrontendPanel items={DEMO_STACK} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Database & API Engineering</h2>
        <p className="mt-1 text-sm text-slate-500">{DATABASE_AREAS.join(' · ')} · {API_AREAS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DatabaseAPIPanel items={DEMO_DATABASE_API} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Engineering & Git Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_ENGINEERING.join(' · ')} · {GIT_GOVERNANCE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIEngineeringPanel items={DEMO_AI_GIT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Code Review & Static Analysis</h2>
        <p className="mt-1 text-sm text-slate-500">{CODE_REVIEW.join(' · ')} · {STATIC_ANALYSIS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CodeReviewPanel items={DEMO_REVIEW} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Secure Coding & Testing</h2>
        <p className="mt-1 text-sm text-slate-500">{SECURE_CODING.join(' · ')} · {TESTING_REQUIREMENTS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SecureTestingPanel items={DEMO_SECURE_TEST} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">CI Gates, Dependencies & Engineering Metrics</h2>
        <p className="mt-1 text-sm text-slate-500">
          {CI_GATES.join(' · ')} · {DEPENDENCY_GOVERNANCE.join(' · ')} · {TECHNICAL_DEBT.join(' · ')} · {ENGINEERING_METRICS.join(' · ')} · {KNOWLEDGE_SHARING.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <CIMetricsPanel items={DEMO_CI_METRICS} />
        </Card>
      </section>
    </div>
  );
}
